import React from 'react';
import { Sparkles, Loader2, Wand2, Maximize2, Minimize2, Download, DollarSign, Grid3x3, Shield } from 'lucide-react';
import VendorOverlayPicker from '../VendorOverlayPicker/VendorOverlayPicker';

// Page header — title, dept badge, Suggest/Spend toggles, Smart suggest,
// vendor overlay picker, Export PDF, Present mode, MITRE/CISA view toggles.
const ZtaHeader = ({
  selectedDepartment,
  suggestionModeEnabled,
  setSuggestionModeEnabled,
  showSpendDrills,
  setShowSpendDrills,
  hasActionableSuggestions,
  handleAutoPopulate,
  autoPopulating,
  suggestionsLoading,
  isRefreshing,
  selectedOverlayVendors,
  toggleOverlayVendor,
  clearOverlayVendors,
  vendorOverlayLoading,
  hasAnyOverlay,
  exportToPDF,
  isExporting,
  togglePresentMode,
  isFullscreen,
  viewMode,
  setViewMode,
}) => {
  return (
    <header className="zta-header">
      <div className="zta-header-left">
        <h1 className="zta-title">Zero Trust Reference Architecture</h1>
        <p className="zta-subtitle">CISA Pillar-Aligned · AB 869 Compliant · Vendor-Agnostic Operating Model</p>
      </div>
      <div className="zta-header-right">
        {selectedDepartment && (
          <span className="zta-dept-badge">
            {selectedDepartment.acronym || selectedDepartment.common_name}
          </span>
        )}
        {/* Suggestion Mode Toggle - hidden toggle for internal use */}
        {selectedDepartment && (
          <button
            className={`zta-suggestion-toggle ${suggestionModeEnabled ? 'active' : ''}`}
            onClick={() => setSuggestionModeEnabled(prev => !prev)}
            title={suggestionModeEnabled ? 'Disable eProcure suggestions' : 'Enable eProcure suggestions'}
          >
            <Sparkles size={14} />
            <span>{suggestionModeEnabled ? 'Suggest: ON' : 'Suggest: OFF'}</span>
          </button>
        )}
        {/* Spend drill-in toggle — flip off while presenting to hide the
            $ icons everywhere on the page at once. */}
        {selectedDepartment && (
          <button
            className={`zta-spend-toggle ${showSpendDrills ? 'active' : ''}`}
            onClick={() => setShowSpendDrills(prev => !prev)}
            title={showSpendDrills ? 'Hide spend drill-in icons' : 'Show spend drill-in icons'}
          >
            <DollarSign size={14} />
            <span>{showSpendDrills ? 'Spend: ON' : 'Spend: OFF'}</span>
          </button>
        )}
        {suggestionModeEnabled && selectedDepartment && hasActionableSuggestions && (
          <button
            className="zta-suggest-btn"
            onClick={() => handleAutoPopulate()}
            disabled={autoPopulating}
            title="Auto-populate signals from purchase history"
          >
            {autoPopulating ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Wand2 size={14} />
            )}
            <span>{autoPopulating ? 'Populating...' : 'Smart suggest'}</span>
          </button>
        )}
        {suggestionModeEnabled && suggestionsLoading && (
          <span className="zta-suggestions-loading" title="Analyzing purchase history...">
            <Sparkles size={14} className="pulse" />
          </span>
        )}
        {isRefreshing && (
          <span className="zta-refreshing" title="Syncing latest data...">
            <Loader2 size={14} className="spin" />
          </span>
        )}
        {/* Multi-vendor overlay picker. Pick any combination of vendors to
            see where their products cover the framework simultaneously. */}
        <VendorOverlayPicker
          selectedVendors={selectedOverlayVendors}
          onToggle={toggleOverlayVendor}
          onClear={clearOverlayVendors}
        />
        {vendorOverlayLoading && hasAnyOverlay && (
          <span className="zta-overlay-loading" title="Loading coverage...">
            <Loader2 size={14} className="spin" />
          </span>
        )}
        {/* Export PDF Button */}
        {selectedDepartment && (
          <button
            className="zta-export-btn"
            onClick={exportToPDF}
            disabled={isExporting}
            title="Export assessment as PDF"
          >
            {isExporting ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
            <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
          </button>
        )}
        <button
          className="zta-fullscreen-btn"
          onClick={togglePresentMode}
          title={isFullscreen ? 'Exit present mode (ESC)' : 'Enter present mode'}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          <span>{isFullscreen ? 'Exit' : 'Present'}</span>
        </button>
        {/* MITRE ATT&CK Matrix view toggle — swaps body for the matrix tab */}
        <button
          className={`zta-fullscreen-btn ${viewMode === 'mitre' ? 'active' : ''}`}
          onClick={() => setViewMode(prev => prev === 'mitre' ? 'architecture' : 'mitre')}
          title={viewMode === 'mitre' ? 'Back to Reference Architecture' : 'Switch to MITRE ATT&CK Matrix'}
        >
          <Grid3x3 size={16} />
          <span>{viewMode === 'mitre' ? 'Architecture' : 'MITRE Matrix'}</span>
        </button>
        {/* CISA Zero Trust Maturity Model view toggle */}
        <button
          className={`zta-fullscreen-btn ${viewMode === 'cisa' ? 'active' : ''}`}
          onClick={() => setViewMode(prev => prev === 'cisa' ? 'architecture' : 'cisa')}
          title={viewMode === 'cisa' ? 'Back to Reference Architecture' : 'Switch to CISA Maturity Model'}
        >
          <Shield size={16} />
          <span>{viewMode === 'cisa' ? 'Architecture' : 'CISA Model'}</span>
        </button>
        <span className="zta-confidential">CONFIDENTIAL</span>
        <span className="zta-version">Version 1.0 | 2025</span>
      </div>
    </header>
  );
};

export default ZtaHeader;
